import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Close, Search, ViewColumnOutlined } from '@mui/icons-material';
import {
  ColumnsPanelTrigger,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  ToolbarButton,
  gridFilteredRowCountSelector,
  gridRowCountSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';

export const ProjectsTableToolbar = () => {
  const apiRef = useGridApiContext();
  const total = useGridSelector(apiRef, gridRowCountSelector);
  const visible = useGridSelector(apiRef, gridFilteredRowCountSelector);

  return (
    <Toolbar aria-label="Project library controls">
      <QuickFilter
        expanded
        debounceMs={150}
        render={
          <Box
            sx={{
              flex: { xs: '1 0 100%', sm: '1 1 240px' },
              maxWidth: { xs: '100%', sm: 340 },
            }}
          />
        }
      >
        <QuickFilterControl
          render={({ ref, slotProps, ...controlProps }, state) => (
            <TextField
              {...controlProps}
              inputRef={ref}
              fullWidth
              size="small"
              placeholder="Search projects…"
              slotProps={{
                htmlInput: {
                  ...slotProps?.htmlInput,
                  'aria-label': 'Search projects',
                },
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 19, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: state.value ? (
                    <InputAdornment position="end">
                      <QuickFilterClear
                        aria-label="Clear search"
                        tabIndex={0}
                        render={<IconButton size="small" />}
                      >
                        <Close sx={{ fontSize: 16 }} />
                      </QuickFilterClear>
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
          )}
        />
      </QuickFilter>

      <Typography
        component="span"
        variant="caption"
        aria-live="polite"
        aria-atomic="true"
        sx={{
          ml: { xs: 0, sm: 'auto' },
          mr: { xs: 'auto', sm: 0 },
          color: 'text.secondary',
          fontSize: 12,
          whiteSpace: 'nowrap',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {visible === total ? total : `${visible} of ${total}`}{' '}
        {total === 1 ? 'project' : 'projects'}
      </Typography>

      <ColumnsPanelTrigger
        render={
          <ToolbarButton
            render={
              <Button
                variant="neutral"
                size="small"
                startIcon={<ViewColumnOutlined sx={{ fontSize: 17 }} />}
                sx={{ height: 38, fontSize: 12 }}
              />
            }
          />
        }
      >
        Columns
      </ColumnsPanelTrigger>
    </Toolbar>
  );
};

export const ProjectsNoResults = () => {
  const apiRef = useGridApiContext();

  return (
    <Box
      sx={{
        height: '100%',
        display: 'grid',
        placeContent: 'center',
        textAlign: 'center',
        p: 3,
      }}
    >
      <Search
        sx={{ mx: 'auto', mb: 1.5, color: 'text.secondary', fontSize: 28 }}
      />
      <Typography variant="body2" fontWeight={600}>
        No matching projects
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 0.5, mb: 1.5 }}
      >
        Try another name or clear your search.
      </Typography>
      <Button
        size="small"
        onClick={() => apiRef.current.setQuickFilterValues([])}
      >
        Clear search
      </Button>
    </Box>
  );
};
